// Una tarjeta de referencia: Añadir producto, Añadir modelo o Añadir referencia. Nació
// adentro de `CajaDeRedes` (ADR-2026-10-01-modo-redes) y se extrajo tal cual para que la
// barra de Editar del visor use LAS MISMAS tarjetas
// (ADR-2026-10-06-editar-con-referencias-y-escritor). Sin `tema`, es byte a byte la de
// Redes; `tema: 'oscuro'` es la variante sobre el vidrio del visor.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Package, Play, X } from 'lucide-react';
import type { ChatAttachment } from '../../agentAttachments';
import { fotosDelMoodboard, miniaturaDeAdjunto } from '../../etiquetaDeAdjunto';
import { CarpetaDeImagenes } from './CarpetaDeImagenes';

/** Los colores de cada tema. `claro` es la tarjeta de Redes, sin cambios. */
const TEMAS = {
  claro: {
    boton: 'rounded-xl bg-[#00000009]',
    icono: 'bg-white',
    iconoColor: 'text-brand-primary',
    titulo: 'text-brand-black',
    subtitulo: 'text-[#02062B]/[0.45]',
    chevron: 'text-brand-black/80',
    quitar: 'bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.1] hover:text-brand-black',
    anillo: 'ring-white',
    fondoMiniatura: 'bg-neutral-100',
  },
  // Sobre el vidrio oscuro del visor. Radio 20: la pastilla es de 28 y las tarjetas van a
  // 8 px del borde (regla concéntrica).
  oscuro: {
    boton: 'rounded-[20px] bg-white/[0.08] hover:bg-white/[0.12]',
    icono: 'bg-white/10',
    iconoColor: 'text-white',
    titulo: 'text-white',
    subtitulo: 'text-white/50',
    chevron: 'text-white/40',
    quitar: 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white',
    anillo: 'ring-black/40',
    fondoMiniatura: 'bg-white/10',
  },
} as const;

/**
 * Una tarjeta de referencia, adentro de la caja y arriba del prompt: Añadir producto, Añadir
 * modelo o Añadir referencia. Es la
 * card de "elegir el producto" de los modos en el teléfono (`PasoUnoMobile`): baja (72 px),
 * horizontal, con el ícono a la izquierda —o el ABANICO de miniaturas de lo elegido—, el
 * texto en el medio y la flecha a la derecha. Con algo elegido, la flecha se vuelve la X que lo
 * quita; tocar la tarjeta siempre abre el selector del Agente en su pestaña.
 */
/** Compacta en el teléfono: lo de la izquierda (42 px) se dibuja al 62 % (~26 px) y el
 *  margen negativo le devuelve el lugar que la escala no achica
 *  (ADR-2026-10-09-asistente-en-crear-7; más chico en ADR-2026-10-12-iconos-de-las-tarjetas-mas-chicos). */
const ACHICADA = 'max-md:-m-[8px] max-md:scale-[0.62]';
/** `chica` (el panel lateral del Asistente): lo de la izquierda al 82 %, en todos los anchos
 *  (ADR-2026-10-13-las-tarjetas-de-crear-en-el-panel-del-asistente). */
const EN_EL_PANEL = '-m-[4px] scale-[0.82]';

export const TarjetaDeReferencia: React.FC<{
  rotulo: string;
  Icono: typeof Package;
  /** Lo elegido de esta tarjeta (uno, o varias imágenes subidas). */
  adjuntos: ChatAttachment[];
  onAbrir: () => void;
  onQuitar: () => void;
  /**
   * La carpetita con fotos en vez del ícono, mientras NO hay nada elegido: asoman las
   * últimas que tiene la cuenta (o el `relleno`) y al pasar el mouse por la tarjeta se
   * abren en abanico. Con algo elegido la carpeta se va y quedan las miniaturas de eso.
   */
  carpeta?: { imagenes: string[]; tono: 'azul' | 'celeste' | 'cian' | 'violeta' | 'gris'; relleno?: React.ReactNode };
  /** Marca la tarjeta como destino de la animación de "Editar" (`data-destino-referencia`). */
  esDestino?: boolean;
  /** Un texto que pisa al de siempre mientras pasa algo ("Subiendo…"). */
  estado?: string;
  /** Texto descriptivo breve debajo del rótulo, cuando no hay nada elegido. */
  descripcion?: string;
  /** Tamaño del contenedor del ícono/carpeta en px. Default 42. */
  tamanoIcono?: number;
  /** `claro` (Redes, default) u `oscuro` (la barra del visor). */
  tema?: keyof typeof TEMAS;
  /**
   * En el teléfono, la tarjeta se achica y queda acostada: ícono chico a la izquierda y el
   * rótulo corto ("Producto", "Modelo", "Referencia") al lado, sin flecha, en 46 px de alto
   * (ADR-2026-10-09-asistente-en-crear-7; antes iba parada, ícono arriba y rótulo abajo).
   * En computadora no cambia nada.
   */
  compactaEnTelefono?: boolean;
  /** El fondo de la tarjeta, si no es el gris del tema (Crear tiñe cada una de su azul). */
  fondo?: string;
  /** Más baja (52 px) y con el ícono más chico, en todos los anchos: para el panel lateral. */
  chica?: boolean;
  /**
   * Con un video al frente, un ▶ sobre su miniatura que lo reproduce
   * (ADR-2026-10-20-el-video-adjunto-se-ve-y-se-reproduce). Sin esto, como siempre.
   */
  onVerVideo?: (video: Extract<ChatAttachment, { kind: 'video' }>) => void;
}> = ({ rotulo, Icono, adjuntos, onAbrir, onQuitar, carpeta, esDestino = false, estado, descripcion, tamanoIcono, tema = 'claro', compactaEnTelefono = false, fondo, chica = false, onVerVideo }) => {
  const t = TEMAS[tema];
  const compacta = compactaEnTelefono;
  const [hover, setHover] = useState(false);
  // Cada vez que se SUMA algo (un id que antes no estaba), la miniatura y el texto rebotan
  // (ADR-2026-10-04-rebote-al-anadir-un-adjunto). El contador va de `key`, así la animación
  // vuelve a correr aunque la tarjeta ya tuviera algo; quitar no la dispara, y lo que ya
  // estaba al montar tampoco.
  const [pulso, setPulso] = useState(0);
  const idsPrevios = useRef<string[] | null>(null);
  useEffect(() => {
    const ids = adjuntos.map((a) => a.id);
    const previos = idsPrevios.current;
    idsPrevios.current = ids;
    if (previos && ids.some((id) => !previos.includes(id))) setPulso((p) => p + 1);
  }, [adjuntos]);
  const rebote = pulso > 0 ? 'animate-rebote-de-adjunto' : '';
  const reboteDeTexto = pulso > 0 ? 'animate-rebote-de-texto' : '';
  // El abanico es sólo de lo ELEGIDO; vacía, la tarjeta lleva el ícono (o la carpeta).
  const elegidas = adjuntos.map((a) => miniaturaDeAdjunto(a)).filter((u): u is string => !!u);
  const abanico = elegidas.slice(0, 3);
  // La carpeta sólo mientras NO hay nada elegido; con algo, el abanico de sus miniaturas.
  const fotosDeCarpeta = carpeta && adjuntos.length === 0 ? carpeta.imagenes.slice(0, 3) : [];
  // Qué dice la tarjeta: su rótulo vacía; con una cosa, cuál; con varias, cuántas. Las
  // referencias pueden mezclar imágenes y un video.
  const esMedia = (a: ChatAttachment) => a.kind === 'image' || a.kind === 'video';
  const unidad = rotulo.replace(/^Añadir /, '').toLowerCase();
  const texto = estado
    ? estado
    : adjuntos.length === 0
      ? rotulo
      : adjuntos.length === 1
        ? adjuntos[0].kind === 'image'
          ? '1 imagen'
          : adjuntos[0].kind === 'video'
            ? '1 video'
            : adjuntos[0].name
        : adjuntos.every((a) => a.kind === 'image')
          ? `${adjuntos.length} imágenes`
          : adjuntos.every(esMedia)
            ? `${adjuntos.length} referencias`
            : `${adjuntos.length} ${unidad}s`;
  // Debajo, de qué tarjeta es. Una carpeta de moodboard sola dice cuántas fotos lleva
  // (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes-2).
  const solo = adjuntos.length === 1 ? adjuntos[0] : null;
  const primero = adjuntos[0];
  const subtitulo = solo?.kind === 'moodboard' ? `Estilo · ${fotosDelMoodboard(solo)}` : rotulo.replace(/^Añadir /, '');
  return (
    <div className="group relative min-w-0 flex-1" {...(esDestino ? { 'data-destino-referencia': '' } : {})}>
      <button
        type="button"
        onClick={onAbrir}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        // Con algo elegido, la X (28 px a 12 px del borde) va encima de la tarjeta: `pr-12` le
        // deja su lugar, así un nombre largo se corta con "…" antes de llegar y no se pisan.
        className={`flex w-full items-center ${chica ? 'h-[52px] gap-2 px-2.5' : 'h-[64px] gap-3 px-3'} ${fondo ? `rounded-xl ${fondo}` : t.boton} ${adjuntos.length > 0 ? (chica ? 'pr-10' : 'pr-12') : chica ? 'pr-3' : 'pr-3.5'} text-left transform transition-transform duration-300 hover:-translate-y-0.5 ${
          compacta ? `max-md:h-[46px] max-md:gap-1 max-md:pl-1.5 ${adjuntos.length > 0 ? 'max-md:pr-7' : 'max-md:pr-1.5'}` : ''
        }`}
      >
        {carpeta && adjuntos.length === 0 && (fotosDeCarpeta.length > 0 || carpeta.relleno) ? (
          <span className={`grid h-[42px] w-[42px] shrink-0 place-items-center ${compacta ? ACHICADA : ''} ${chica ? EN_EL_PANEL : ''}`}>
            <CarpetaDeImagenes imagenes={fotosDeCarpeta} relleno={carpeta.relleno} tono={carpeta.tono} abierta={hover} ancho={36} alto={27} />
          </span>
        ) : abanico.length > 0 ? (
          // Las miniaturas se pisan un poco entre sí (-ml) para leerse como un grupo.
          <span key={pulso} className={`inline-flex shrink-0 items-center ${rebote} ${compacta ? ACHICADA : ''} ${chica ? EN_EL_PANEL : ''}`}>
            {abanico.map((url, i) => (
              <span key={`${url}:${i}`} className={`block overflow-hidden rounded-[12px] ${t.fondoMiniatura} ring-2 ${t.anillo} ${i > 0 ? '-ml-3' : ''}`} style={{ width: tamanoIcono ?? 42, height: tamanoIcono ?? 42 }}>
                <img src={url} alt="" className="h-full w-full object-cover" />
              </span>
            ))}
          </span>
        ) : (
          <span className={`grid h-[42px] w-[42px] shrink-0 place-items-center rounded-lg ${t.icono} ${compacta ? ACHICADA : ''} ${chica ? EN_EL_PANEL : ''}`}>
            <Icono className={`h-5 w-5 ${t.iconoColor}`} strokeWidth={2} />
          </span>
        )}
        <span key={`t${pulso}`} className={`min-w-0 flex-1 ${reboteDeTexto}`}>
          {/* Compacta en el teléfono, vacía dice sólo "Producto" / "Modelo" / "Referencia". */}
          <span className={`block truncate text-[13.5px] font-semibold ${t.titulo} ${compacta ? 'max-md:hidden' : ''}`}>{texto}</span>
          {compacta && (
            <span className={`block truncate text-[11.5px] font-semibold tracking-[-0.01em] md:hidden ${t.titulo}`}>
              {adjuntos.length === 0 && !estado ? unidad.charAt(0).toUpperCase() + unidad.slice(1) : texto}
            </span>
          )}
          {adjuntos.length > 0
            ? <span className={`block truncate text-[12px] ${t.subtitulo} ${compacta ? 'max-md:hidden' : ''}`}>{subtitulo}</span>
            : descripcion && <span className={`block text-[12px] ${t.subtitulo} ${compacta ? 'max-md:hidden' : ''}`}>{descripcion}</span>}
        </span>
        {adjuntos.length === 0 && <ChevronRight className={`h-4 w-4 shrink-0 ${t.chevron} ${compacta ? 'max-md:hidden' : ''}`} />}
      </button>
      {/* El ▶ va AFUERA del botón de la tarjeta (un botón no puede ir adentro de otro),
          calcado sobre la primera miniatura: mismo borde izquierdo y mismo lado que ella en
          cada variante (42 px; 26 en el teléfono compacto, que la escala a 0.62; 34 en la
          chica, a 0.82). */}
      {/* Sólo si lo que se ve al frente ES el video: su miniatura, o el ícono de la tarjeta
          cuando no hay ninguna. Un video sin miniatura detrás de una foto no lleva ▶ encima
          de la foto. */}
      {onVerVideo && primero?.kind === 'video' && (miniaturaDeAdjunto(primero) || abanico.length === 0) && (
        <button
          type="button"
          onClick={() => onVerVideo(primero)}
          aria-label={`Ver ${primero.name}`}
          className={`group/ver absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-[12px] ${
            chica ? 'left-2.5 h-[34px] w-[34px]' : 'left-3 h-[42px] w-[42px]'
          } ${compacta ? 'max-md:left-1.5 max-md:h-[26px] max-md:w-[26px]' : ''}`}
          style={tamanoIcono && !chica ? { width: tamanoIcono, height: tamanoIcono } : undefined}
        >
          <span className={`flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white transition-transform group-hover/ver:scale-110 ${compacta ? 'max-md:h-4 max-md:w-4' : ''}`}>
            <Play className={`h-3 w-3 translate-x-[1px] fill-current ${compacta ? 'max-md:h-2 max-md:w-2' : ''}`} aria-hidden="true" />
          </span>
        </button>
      )}
      {adjuntos.length > 0 && (
        <button
          type="button"
          onClick={onQuitar}
          aria-label={`Quitar ${rotulo.toLowerCase()}`}
          className={`absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full ${t.quitar} transition-colors ${
            compacta ? 'max-md:right-1.5 max-md:h-5 max-md:w-5' : chica ? '!right-2 !h-6 !w-6' : ''
          }`}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};

export default TarjetaDeReferencia;
